<template>
  <div>
    <div
      class="wrapper"
      @click="clickHandler"
    >
      <input
        ref="fileRef"
        type="file"
        class="hidden"
        @change="onChange"
      >
      <b-spinner
        v-if="uploading"
        variant="primary"
        small
      />
      <span v-else-if="!uploadedFile">Click here to upload</span>
      <div
        v-else-if="isImageFile && previewUrl"
        class="image-preview-container"
      >
        <img
          :src="previewUrl"
          :alt="uploadedFile.name"
          class="image-preview"
        >
        <span class="file-name">{{ uploadedFile.name }}</span>
      </div>
      <span v-else>{{ uploadedFile.name }}</span>
    </div>

    <small>Allowed Extensions:
      <br>
      <span v-if="!allowedExtensions">Images: {{ imageExtensions.map(type =>
        type.replace('image/', '').toUpperCase()
      ).join(', ') }}</span>

      <br v-if="!allowedExtensions"> {{ allowVideosExtension ? `Videos: ${videoExtensions.map(type =>
        type.replace('video/', '').toUpperCase()
      ).join(', ')}` : '' }}</small>

    <span v-if="allowedExtensions">{{ allowedExtensions.map(type =>
      type.replace('image/', '').toUpperCase()
    ).join(', ') }}</span>

    <template v-if="size">
      <br>
      <span>Allowed Size: {{ size.width }}*{{ size.height }}</span>
    </template>

    <template v-if="maxSizeKB">
      <br>
      <small class="text-muted">File Size Limits: Images ({{ maxSizeKB.image }}KB max), JSON ({{ maxSizeKB.json }}KB max)</small>
    </template>
  </div>
</template>

<script>
import { ref } from '@vue/composition-api'
import { BSpinner } from 'bootstrap-vue'
import axios from 'axios'
import { errorNotification } from '@/utils/toast-notifications'
import axiosIns from '@/libs/axios'

function validateImageSize(file, requiredSize) {
  // eslint-disable-next-line consistent-return
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith('image/')) {
      return resolve(true) // skip size check for non-images
    }

    const reader = new FileReader()

    reader.onload = e => {
      const img = new Image()
      img.onload = () => {
        const isValid = img.width === requiredSize.width && img.height === requiredSize.height

        if (!isValid) {
          reject(
            new Error(
              `Invalid image size. Required: ${requiredSize.width}x${requiredSize.height}, got: ${img.width}x${img.height}`,
            ),
          )
        } else {
          resolve(true)
        }
      }
      img.onerror = () => reject(new Error('Failed to load image.'))
      img.src = e.target?.result
    }

    reader.onerror = () => reject(new Error('Failed to read file.'))
    reader.readAsDataURL(file)
  })
}

function validateFileSize(file, maxSizeKB) {
  const maxSizeJSON = maxSizeKB.json * 1024 // Convert KB to bytes
  const maxSizeImage = maxSizeKB.image * 1024 // Convert KB to bytes

  if (file.type === 'application/json' || file.name.endsWith('.json')) {
    if (file.size > maxSizeJSON) {
      throw new Error(`JSON file size exceeds ${maxSizeKB.json}KB. Current size: ${Math.round(file.size / 1024)}KB`)
    }
  } else if (file.type.startsWith('image/')) {
    if (file.size > maxSizeImage) {
      throw new Error(`Image file size exceeds ${maxSizeKB.image}KB. Current size: ${Math.round(file.size / 1024)}KB`)
    }
  }
}

function validateCanvasDimensions(file, requiredSize) {
  // eslint-disable-next-line consistent-return
  return new Promise((resolve, reject) => {
    if (file.type !== 'application/json' && !file.name.endsWith('.json')) {
      return resolve(true) // skip canvas check for non-JSON files
    }

    const reader = new FileReader()

    reader.onload = e => {
      try {
        const jsonContent = JSON.parse(e.target.result)

        // Check if it's a Lottie animation with canvas dimensions
        if (jsonContent.w && jsonContent.h) {
          const canvasWidth = jsonContent.w
          const canvasHeight = jsonContent.h
          const isValid = canvasWidth === requiredSize.width && canvasHeight === requiredSize.height

          if (!isValid) {
            reject(
              new Error(
                `Invalid canvas dimensions. Required: ${requiredSize.width}x${requiredSize.height}, got: ${canvasWidth}x${canvasHeight}`,
              ),
            )
          } else {
            resolve(true)
          }
        } else {
          // If no canvas dimensions found, we'll allow it (might not be a Lottie file)
          resolve(true)
        }
      } catch (parseError) {
        reject(new Error('Invalid JSON file format'))
      }
    }

    reader.onerror = () => reject(new Error('Failed to read JSON file.'))
    reader.readAsText(file)
  })
}

export default {
  components: {
    BSpinner,
  },
  props: {
    allowVideosExtension: {
      type: Boolean,
      default: false,
    },
    allowedExtensions: {
      type: Array,
      default: undefined,
    },
    size: {
      type: Object,
      default: null,
    },
    maxSizeKB: {
      type: Object,
      default: null,
    },
  },
  setup(props, ctx) {
    const uploadedFile = ref()
    const previewUrl = ref(null)
    const isImageFile = ref(false)
    const imageExtensions = ['image/jpg', 'image/jpeg', 'image/png', 'image/gif', 'image/bmp', 'image/svg']
    const videoExtensions = ['video/mp4', 'video/webm', 'video/ogg']
    const uploading = ref(false)

    const clickHandler = () => {
      if (uploading.value) {
        return
      }
      ctx.refs.fileRef.click()
    }
    const resetFile = () => {
      if (previewUrl.value) {
        URL.revokeObjectURL(previewUrl.value)
        previewUrl.value = null
      }
      uploadedFile.value = undefined
      isImageFile.value = false
    }
    const onChange = async ev => {
      const file = ev.target.files[0]
      if (file) {
        // Validate file size first (only if maxSizeKB is provided)
        if (props.maxSizeKB) {
          try {
            validateFileSize(file, props.maxSizeKB)
          } catch (err) {
            errorNotification(err.message)
            return
          }
        }

        // Validate file type
        if (props.allowedExtensions) {
          const mimeType = file.type // e.g., "image/png"
          const extension = mimeType.split('/')[1] // e.g., "png"
          if (!props.allowedExtensions.includes(extension)) {
            errorNotification('Invalid file type')
            return
          }
        } else if (props.allowVideosExtension) {
          if (!videoExtensions.includes(file.type) && !imageExtensions.includes(file.type)) {
            errorNotification('Only video or image extensions allowed')
            return
          }
        } else if (!imageExtensions.includes(file.type)) {
          errorNotification('Invalid file type')
          return
        }

        // Validate dimensions (for both images and JSON canvas)
        if (props.size) {
          try {
            await validateImageSize(file, props.size)
            await validateCanvasDimensions(file, props.size)
          } catch (err) {
            errorNotification(err.message)
            return
          }
        }

        uploadedFile.value = file

        // Create preview URL for image files (excluding JSON/Lottie files)
        if (file.type.startsWith('image/') && file.type !== 'application/json' && !file.name.endsWith('.json')) {
          isImageFile.value = true
          previewUrl.value = URL.createObjectURL(file)
        } else {
          isImageFile.value = false
          previewUrl.value = null
        }

        uploading.value = true
        try {
          const result = await axiosIns.get('/signed-urls', {
            params: {
              fileName: file.name,
              isPublic: true,
            },
          })
          const { url, key } = result.data.data
          await axios.put(url, file)
          ctx.emit('file-uploaded', key)
        } catch (err) {
          errorNotification('Something went wrong')
        } finally {
          uploading.value = false
        }
      }
    }

    return {
      resetFile,
      clickHandler,
      uploadedFile,
      uploading,
      onChange,
      imageExtensions,
      videoExtensions,
      previewUrl,
      isImageFile,
    }
  },
}
</script>

<style scoped>
.wrapper {
    height:40px;
    border: 1px solid #d8d6de;
    border-radius: 25px;
    display:flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    margin-bottom: 4px;
}
.wrapper:has(.image-preview-container) {
    height: auto;
    min-height: 60px;
    padding: 8px;
    flex-direction: column;
    gap: 8px;
}
.hidden {
    display:none;
}
.image-preview-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
}
.image-preview {
    max-width: 100px;
    max-height: 60px;
    object-fit: contain;
    border-radius: 4px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}
.file-name {
    font-size: 12px;
    color: #6c757d;
    text-align: center;
    word-break: break-all;
    max-width: 200px;
}
</style>
